import { Inject } from '@odinjs/odin';

import { APPLICATION_ROUTES, CONFIRMATION_FIELD_VALID, confirmationFieldValidator, Endpoint, passwordNewDifferentCurrent, PW_NEW_DIFFERENT_CURRENT, Return, Route, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { ButtonAPI, FieldAPI, FormAPI, ModalAPI, PanelAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnChange, OnClick, OnClose, OnMount, OnSubmit, OnValidate } from '@philips-emr/tws-core/lib/client';
import { SubmitEvent, ValidateEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { RequestResponse } from '@philips-emr/tws-core/lib/services/http-service';

import { ChangePwAliases as Aliases, FragmentNames } from '../codegen';

import BaseController from './BaseController';

interface User {
  nrSequence: number,
  response: string,
}

@Controller({ fragment: FragmentNames.CHANGE_PW })
export default class ChangePwController extends BaseController {
  @Inject(Aliases.FORM_ALT_PW)
  private formAltPw: FormAPI;
  @Inject(Aliases.FIELD_NEW_PW)
  private fieldNewPw: FieldAPI;
  @Inject(Aliases.FIELD_NEW_PW_CONFIRM)
  private fieldNewPwConfirm: FieldAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toast: ToastNotificationAPI;
  @Inject(Aliases.BUTTON_ALT_PW)
  private buttonAltPw: ButtonAPI;
  @Inject(Aliases.PANEL_MODAL)
  private modal: PanelAPI;
  @Inject(Aliases.PANEL_SUCCESS)
  private panelSuccess: ModalAPI;

  @OnMount(Aliases.FORM_ALT_PW)
  onMountModalAltPw(): void {
    this.fieldNewPwConfirm.addLocalValidator(CONFIRMATION_FIELD_VALID, (value) => {
      const confirmationValue = this.formAltPw.getRecord()['newPassword'] ?? '';
      return confirmationFieldValidator(value, confirmationValue);
    });

    this.fieldNewPw.addLocalValidator(PW_NEW_DIFFERENT_CURRENT, (value) => {
      const pwAtual = this.formAltPw.getRecord()['currentPassword'] ?? '';
      return passwordNewDifferentCurrent(value, pwAtual);
    });
  }

  @OnChange(Aliases.FORM_ALT_PW)
  onChangeForm(): void {
    this.toast.setVisible(false);
  }

  @OnValidate(Aliases.FORM_ALT_PW)
  onValidateForm(event: ValidateEvent): void {
    const { valid } = event;

    this.buttonAltPw.setEnabled(valid);
  }

  @OnSubmit(Aliases.FORM_ALT_PW)
  onSubmitFormAltPw(event: SubmitEvent): void {
    const { record } = event;
    if (this.formAltPw.isValid()) {
      this.buttonAltPw.setEnabled(false);
      this.buttonAltPw.setState({ loading: true });
      const { nrSeqUser } = this.historyService.getCurrentRouteParams();
      HttpService.post<Return<string>>(Endpoint.POST.CHANGE_PASSWORD, { nrSeqUser }, { body: record })
        .then(() => {
          this.buttonAltPw.setState({ loading: false });
          this.toast.setLevel(ToastLevel.SUCCESS);
          this.toast.setVisible(true);
          this.toast.setMessage(`${MessageService.translate('pa.change-made-successfully')} ${MessageService.translate('stipulator.aguarde-redirecionando')}`);
          this.modal.setVisible(false);

          this.historyService.push(Route.MY_DATA);
          ToastServiceProviderAccreditation.successBubble(MessageService.translate('pa.change-made-successfully'));

        })
        .catch(resp => {
          const { body: message } = resp;
          if (message) {
            this.buttonAltPw.setState({ loading: false });
            this.toast.setVisible(true);
            this.toast.setMessage(message.response);
            this.formAltPw.setEditing(true);
          }
        });
    }
  }

  @OnClick(Aliases.BUTTON_ALT_PW)
  onClickAltPw(): void {
    const { nrSequence } = this.historyService.getCurrentRouteParams() as unknown as User;
    this.pwChange(nrSequence);
  }

  @OnClick(Aliases.BUTTON_OK)
  onClickOk(): void {
    this.historyService.push(APPLICATION_ROUTES.PROVIDER_ACCREDITATION);
  }

  @OnClose(Aliases.MODAL_SUCCESS)
  onCloseSuccess(): void {
    this.historyService.push(APPLICATION_ROUTES.PROVIDER_ACCREDITATION);
  }

  @OnClose(Aliases.MODAL_PWS)
  onCloseModal(): void {
    this.modal.setVisible(false);
    this.historyService.push(Route.MY_DATA);
  }

  pwChange(nrSequence: number): void {
    if (nrSequence) {
      this.renewPassword(nrSequence);
    } else {
      this.formAltPw.submit();
    }
  }

  renewPassword(nrSequence: number): void {
    const body = this.formAltPw.getRecord();
    HttpService.post(Endpoint.POST.RENEW_PASSWORD_BY_NRSEQ, { nrSequence }, { body })
      .then(() => {
        this.handleSuccess();
      }).catch(resp => {
        this.handleFailure(resp);
      });
  }

  handleSuccess(): void {
    this.panelSuccess.setVisible(true);
    this.modal.setVisible(false);
  }

  handleFailure(resp: RequestResponse<unknown>): void {
    this.formAltPw.setEditing(true);
    this.buttonAltPw.setState({ loading: false });
    const { response } = resp.body as User;
    this.toast.setLevel(ToastLevel.WARNING);
    this.toast.setVisible(true);
    this.toast.setMessage(response);
  }
}
